import { useEffect, useState } from 'react';
import { transactionApi } from '../../../services/transactionApi';
import { billApi } from '../../../services/billApi';
import { goalApi } from '../../../services/goalApi';
import { buildHomeData, HomeData, MONTHS_SHOWN, monthRange, monthsBack, monthsEndingAt, RECENT_SHOWN } from './homeData';

/** Loads the month in focus (YYYY-MM) for the context in scope and shapes it for the home. */
export const useDashboardHome = (contextId: string | undefined, budgetLimit: number, month: string) => {
  const [data, setData] = useState<HomeData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const now = new Date();
        const range = { ...monthRange(month, now), page: 1, limit: 1, contextId };
        // A one-row page still carries the whole month's summary and total count;
        // the unfiltered call brings the month's latest entries along.
        const [all, incomes, expenses, calendar, monthly, bills, goals] = await Promise.all([
          transactionApi.getTransactions({ ...range, limit: RECENT_SHOWN }),
          transactionApi.getTransactions({ ...range, type: 'income' }),
          transactionApi.getTransactions({ ...range, type: 'expense' }),
          transactionApi.getCalendar(month, contextId),
          // the API counts back from today; ask for enough months to reach the one in focus
          transactionApi.getMonthly(MONTHS_SHOWN + monthsBack(month, now), contextId).catch(() => []),
          billApi.list({ status: 'open', contextId }).catch(() => []),
          goalApi.list({ status: 'active', contextId }).catch(() => []),
        ]);
        if (cancelled) return;
        setData(
          buildHomeData(
            {
              summary: all.summary,
              incomeEntries: incomes.total,
              expenseEntries: expenses.total,
              calendar,
              monthly: monthsEndingAt(monthly, month),
              bills,
              goals,
              budgetLimit,
              recent: all.transactions,
            },
            now,
            month,
          ),
        );
        setError(null);
      } catch (loadError) {
        if (cancelled) return;
        setError(loadError as Error);
        setData(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [contextId, budgetLimit, month]);

  return { data, isLoading, error };
};
